import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface Section {
  id: string;
  label: string;
  icon: string;
}

const SECTIONS: Section[] = [
  { id: 'quickstart', label: 'Quick Start',    icon: '▶' },
  { id: 'files',      label: 'Input Files',    icon: '📁' },
  { id: 'bands',       label: 'Band Data',       icon: '◎' },
  { id: 'layouts',    label: 'Layouts',         icon: '⊞' },
  { id: 'options',    label: 'Display Options', icon: '⚙' },
  { id: 'colors',     label: 'Colors',          icon: '◉' },
  { id: 'vcf',        label: 'VCF Support',     icon: '🧬' },
  { id: 'download',   label: 'Downloading',     icon: '↓' },
  { id: 'faq',        label: 'FAQ',             icon: '?' },
];

export const HelpDialog = () => {
  const [open, setOpen]     = useState(false);
  const [active, setActive] = useState('quickstart');

  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const s        = makeStyles(isMobile);

  const scrollTo = (id: string) => {
    setActive(id);
    const el = document.getElementById(`help-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    for (const sec of SECTIONS) {
      const el = document.getElementById(`help-${sec.id}`);
      if (el && el.offsetTop - container.scrollTop <= 80) setActive(sec.id);
    }
  };

  return (
    <>
      <button
        className="btn"
        onClick={() => setOpen(true)}
        aria-label="Open ideochrom user guide"
        aria-haspopup="dialog"
      >
        ? Help
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="help-dialog-title"
        aria-describedby="help-dialog-desc"
        PaperProps={{
          style: {
            height: isMobile ? '100%' : '90vh',
            maxHeight: isMobile ? '100%' : '90vh',
            borderRadius: isMobile ? 0 : 10,
            fontFamily: 'var(--font-sans)',
          },
        }}
      >
        {/* ── Title bar ────────────────────────────────── */}
        <div style={s.titleBar} role="banner">
          <div>
            <h1 id="help-dialog-title" style={s.titleMain}>User Guide</h1>
            <p id="help-dialog-desc" style={s.titleSub}>
              ideochrom — karyotype ideogram generator
            </p>
          </div>
          <IconButton onClick={() => setOpen(false)} size="small" aria-label="Close user guide">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <DialogContent style={{ padding: 0, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden' }}>

          {/* ── Nav (vertical on desktop, horizontal on mobile) ── */}
          <nav aria-label="Help sections" style={s.nav}>
            {SECTIONS.map(sec => (
              <button
                key={sec.id}
                style={{ ...s.navBtn, ...(active === sec.id ? s.navBtnActive : {}) }}
                onClick={() => scrollTo(sec.id)}
                aria-current={active === sec.id ? 'location' : undefined}
              >
                <span style={s.navIcon} aria-hidden="true">{sec.icon}</span>
                <span style={isMobile ? s.navLabelMobile : undefined}>{sec.label}</span>
              </button>
            ))}
          </nav>

          {/* ── Scrollable content ───────────────────────── */}
          <div style={s.body} onScroll={handleScroll} role="main">

            {/* ── Quick Start ─────────────────────────── */}
            <section id="help-quickstart" aria-label="Quick start guide">
              <h2 style={s.h2}>▶ Quick Start</h2>
              <p style={s.lead}>
                Generate a chromosome ideogram in three steps — no account or installation needed.
              </p>
              <div style={s.stepsGrid}>
                {[
                  { n: '1', head: 'Choose a Cytoband File', body: 'Pick a pre-loaded band file from the dropdown (e.g. "400 Band" for human GRCh38), or upload your own tab-delimited .txt file.' },
                  { n: '2', head: 'Add Your Data (Optional)', body: 'Upload a Sample file with copy-number events, a Genes file to mark genes, or a Translocation file for rearrangement arcs.' },
                  { n: '3', head: 'Generate & Download', body: 'Click Generate. Your ideogram appears in the preview area. Choose a format and download — PNG, PDF, TIFF, and more.' },
                ].map(step => (
                  <div key={step.n} style={s.stepCard}>
                    <div style={s.stepNum} aria-hidden="true">{step.n}</div>
                    <strong style={s.stepHead}>{step.head}</strong>
                    <p style={s.stepBody}>{step.body}</p>
                  </div>
                ))}
              </div>
            </section>

            <hr style={s.rule} />

            {/* ── Input Files ─────────────────────────── */}
            <section id="help-files" aria-label="Input file formats">
              <h2 style={s.h2}>📁 Input Files</h2>

              <FileSection
                title="Cytoband File"
                badge="Required"
                badgeColor="var(--accent)"
                desc="Defines the chromosome band structure and Giemsa staining pattern used to draw the ideogram."
                formats={['Pre-loaded (select from dropdown)', 'Custom .txt — tab-delimited']}
                columns={[
                  { name: 'chromosome', desc: 'e.g. 1, chrX, 22' },
                  { name: 'start',      desc: 'Band start position (bp)' },
                  { name: 'end',        desc: 'Band end position (bp)' },
                  { name: 'bandname',   desc: 'e.g. p36.33, q21.1' },
                  { name: 'stain',      desc: 'gneg · gpos25 · gpos50 · gpos75 · gpos100 · acen · gvar · stalk' },
                ]}
                s={s}
              />

              <FileSection
                title="Sample File"
                badge="Optional"
                badgeColor="var(--muted)"
                desc="Copy-number abnormalities for one or more patients. Each row is one event on one chromosome. Events are rendered as coloured bars to the right of each chromosome arm."
                formats={['Tab-delimited .txt', 'Wakhan CNA VCF (.vcf)']}
                columns={[
                  { name: 'patientid',  desc: 'Sample / patient label' },
                  { name: 'chromosome', desc: 'Must match cytoband names' },
                  { name: 'type',       desc: 'Gain · Loss · Deletion · Homo_Loss · CN_LOH' },
                  { name: 'start',      desc: 'Event start (bp)' },
                  { name: 'end',        desc: 'Event end (bp)' },
                ]}
                note="You can also upload a Wakhan CNA VCF directly — it is detected automatically and LOSS / GAIN / CNLOH events are mapped to the corresponding abnormality types."
                s={s}
              />

              <FileSection
                title="Genes File"
                badge="Optional"
                badgeColor="var(--muted)"
                desc="Annotates specific genes on the ideogram with small red tick marks and labels."
                formats={['Tab-delimited .txt']}
                columns={[
                  { name: 'gene / name / genename', desc: 'Gene symbol (e.g. BRCA1, TP53)' },
                  { name: 'chromosome',             desc: 'Must match cytoband names' },
                  { name: 'start',                  desc: 'Gene start (bp)' },
                  { name: 'end',                    desc: 'Gene end (bp)' },
                ]}
                s={s}
              />

              <FileSection
                title="Translocation File"
                badge="Circular only"
                badgeColor="var(--blue)"
                desc="Draws arcs through the centre of a circular ideogram linking two chromosomal regions. Useful for illustrating rearrangements."
                formats={['Tab-delimited .txt / .tsv', 'Severus SV VCF (.vcf)']}
                columns={[
                  { name: 'chr1',   desc: 'Chromosome of breakpoint 1' },
                  { name: 'start1', desc: 'Start position (bp)' },
                  { name: 'end1',   desc: 'End position (bp)' },
                  { name: 'chr2',   desc: 'Chromosome of breakpoint 2' },
                  { name: 'start2', desc: 'Start position (bp)' },
                  { name: 'end2',   desc: 'End position (bp)' },
                  { name: 'color',  desc: 'Optional hex colour, e.g. #FF6600' },
                ]}
                note="You can also upload a Severus SV VCF directly — BND breakend pairs are extracted and drawn as arcs, automatically coloured by rearrangement class (inv_tra, complex_inv, foldback)."
                s={s}
              />
            </section>

            <hr style={s.rule} />

            {/* ── Band Data ───────────────────────────── */}
            <section id="help-bands" aria-label="Pre-loaded band data">
              <h2 style={s.h2}>◎ Pre-loaded Band Data</h2>
              <p style={s.para}>
                The pre-loaded cytoband files in the dropdown are sourced from the{' '}
                <strong>NCBI cytogenetic data repository</strong> at{' '}
                <code style={s.code}>ftp.ncbi.nlm.nih.gov/pub/cyto/data/</code>.
                This is the authoritative public dataset maintained by NCBI containing ideogram band
                coordinates for human and other organisms across multiple genome assemblies and
                band resolutions.
              </p>
              <h3 style={s.h3}>Update schedule</h3>
              <p style={s.para}>
                The server checks the NCBI directory once per day at midnight (UTC) for new or
                updated files. Any files added to the NCBI repository since the last check are
                downloaded, validated, and made available in the dropdown automatically — no
                manual action is required.
              </p>
              <h3 style={s.h3}>What's included</h3>
              <div style={s.colorGrid}>
                {[
                  { label: '400 Band', desc: 'Low-resolution — suitable for overview karyograms and presentations' },
                  { label: '550 Band', desc: 'Medium-resolution — standard for most clinical and research figures' },
                  { label: '850 Band', desc: 'High-resolution — maximum detail, best paired with Band Labels enabled' },
                ].map(r => (
                  <div key={r.label} style={s.colorRow}>
                    <span style={{ ...s.swatch, background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--ink)', fontSize: '.58rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44 }}>
                      {r.label}
                    </span>
                    <span style={s.colorLabel}>{r.desc}</span>
                  </div>
                ))}
              </div>
              <p style={{ ...s.para, marginTop: 14 }}>
                Files for non-human species (mouse, rat, Drosophila, and others) are included when
                available in the NCBI dataset and are labelled with their genome assembly name
                (e.g. GRCm38, Rnor_6.0).
              </p>
            </section>

            <hr style={s.rule} />

            {/* ── Layouts ─────────────────────────────── */}
            <section id="help-layouts" aria-label="Layout options">
              <h2 style={s.h2}>⊞ Layouts</h2>
              <div style={s.twoCol}>
                <div style={s.card}>
                  <strong style={s.cardHead}>Linear (default)</strong>
                  <p style={s.cardBody}>
                    Chromosomes are arranged in a grid — six per row — scaled to their relative lengths. Each chromosome shows its G-band pattern, with copy-number events drawn as vertical coloured bars to the right and gene tick marks on the left. Best for classic karyotype presentations and publication figures.
                  </p>
                </div>
                <div style={s.card}>
                  <strong style={s.cardHead}>Circular (Circos-style)</strong>
                  <p style={s.cardBody}>
                    Chromosomes are arranged in a ring proportional to their base-pair length. Copy-number tracks appear as concentric arcs inside the band ring. Translocation arcs are drawn as Bézier curves through the centre. Ideal for visualising genome-wide structural rearrangements in cancer and complex genomics.
                  </p>
                  <p style={s.cardNote}>The Translocation File slot appears only when Circular is selected.</p>
                </div>
              </div>
            </section>

            <hr style={s.rule} />

            {/* ── Display Options ──────────────────────── */}
            <section id="help-options" aria-label="Display options">
              <h2 style={s.h2}>⚙ Display Options</h2>
              <dl style={s.dl}>
                <dt style={s.dt}>Hide Unused Abnormality Types</dt>
                <dd style={s.dd}>When checked, removes abnormality types from the legend that have no events in the loaded sample file. Keeps the legend clean.</dd>

                <dt style={s.dt}>Chromosome Filter</dt>
                <dd style={s.dd}>Check chromosomes to exclude them from the output entirely. Useful for focusing on specific chromosomes or removing chrY from a female sample.</dd>

                <dt style={s.dt}>Abnormality Filter</dt>
                <dd style={s.dd}>Uncheck any event type (e.g. Gain, Loss) to hide it from both the drawing and the legend — without removing it from the underlying data.</dd>

                <dt style={s.dt}>Band Labels</dt>
                <dd style={s.dd}>When enabled, cytogenetic band names (e.g. p36.33, q21.1) are printed alongside each band on the chromosome body. Most useful with high-resolution band files (550 or 850 Band).</dd>

                <dt style={s.dt}>Preview</dt>
                <dd style={s.dd}>Renders a lower-resolution preview in your browser before generating the final high-resolution image. Useful for quickly checking layout before committing to a large export.</dd>
              </dl>
            </section>

            <hr style={s.rule} />

            {/* ── Colors ──────────────────────────────── */}
            <section id="help-colors" aria-label="Color customization">
              <h2 style={s.h2}>◉ Color Customization</h2>
              <p style={s.para}>
                Every color in the ideogram can be changed. Open the <strong>Abnormality Colors</strong> or <strong>Band Stain Colors</strong> accordion in the Options panel to see color swatches for each type. Click a swatch to open the color picker.
              </p>

              <h3 style={s.h3}>Default Abnormality Colors</h3>
              <div style={s.colorGrid}>
                {[
                  { type: 'Gain',      hex: '#0000FF', label: 'copy number increase' },
                  { type: 'Loss',      hex: '#FF0000', label: 'single-copy deletion' },
                  { type: 'Deletion',  hex: '#FF0000', label: 'same as Loss by default' },
                  { type: 'Homo Loss', hex: '#FF8C00', label: 'both copies deleted (CN=0)' },
                  { type: 'CN LOH',   hex: '#008000', label: 'copy-neutral loss of heterozygosity' },
                ].map(c => (
                  <div key={c.type} style={s.colorRow}>
                    <span style={{ ...s.swatch, background: c.hex }} aria-hidden="true" />
                    <span style={s.colorLabel}><strong>{c.type}</strong> — {c.label}</span>
                  </div>
                ))}
              </div>

              <h3 style={s.h3}>Default Band Stain Colors</h3>
              <div style={s.colorGrid}>
                {[
                  { stain: 'gneg',    hex: '#FFFFFF', label: 'Negatively staining — white' },
                  { stain: 'gpos25',  hex: '#C8C8C8', label: 'Lightly staining — light grey' },
                  { stain: 'gpos50',  hex: '#C8C8C8', label: 'Moderately staining — light grey' },
                  { stain: 'gpos75',  hex: '#828282', label: 'Darkly staining — dark grey' },
                  { stain: 'gpos100', hex: '#000000', label: 'Heavily staining — black' },
                  { stain: 'acen',    hex: '#D92F27', label: 'Centromere — red' },
                  { stain: 'gvar',    hex: '#DCDCDC', label: 'Variable heterochromatin' },
                  { stain: 'stalk',   hex: '#C8C8C8', label: 'Stalks (NOR regions)' },
                ].map(c => (
                  <div key={c.stain} style={s.colorRow}>
                    <span style={{ ...s.swatch, background: c.hex, border: c.hex === '#FFFFFF' ? '1px solid #ccc' : undefined }} aria-hidden="true" />
                    <span style={s.colorLabel}><strong>{c.stain}</strong> — {c.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <hr style={s.rule} />

            {/* ── VCF Support ─────────────────────────── */}
            <section id="help-vcf" aria-label="VCF file support">
              <h2 style={s.h2}>🧬 VCF File Support</h2>
              <p style={s.para}>
                Ideochrom reads VCF files directly from two widely-used long-read cancer genomics tools — no conversion step needed. VCF format is detected automatically from the file header.
              </p>

              <div style={s.twoCol}>
                <div style={s.card}>
                  <strong style={s.cardHead}>Wakhan CNA VCF → Sample File slot</strong>
                  <p style={s.cardBody}>
                    Wakhan produces phased, haplotype-aware copy-number segments. Upload the <code style={s.code}>*_wakhan_cna_integers.vcf</code> file in the <strong>Sample File</strong> slot. Events are mapped automatically:
                  </p>
                  <ul style={s.ul}>
                    <li><code style={s.code}>LOSS</code> + GT=0/1 → <strong>Loss</strong></li>
                    <li><code style={s.code}>LOSS</code> + GT=1/1 → <strong>Homo Loss</strong></li>
                    <li><code style={s.code}>GAIN</code> → <strong>Gain</strong></li>
                    <li><code style={s.code}>CNLOH</code> → <strong>CN LOH</strong></li>
                  </ul>
                </div>
                <div style={s.card}>
                  <strong style={s.cardHead}>Severus SV VCF → Translocation File slot</strong>
                  <p style={s.cardBody}>
                    Severus calls structural variants from long-read data. Upload the <code style={s.code}>severus_somatic.vcf</code> in the <strong>Translocation File</strong> slot (Circular layout only). BND breakend pairs become arcs coloured by rearrangement class:
                  </p>
                  <ul style={s.ul}>
                    <li><span style={{ color: '#E06020' }} aria-hidden="true">■</span> <strong>inv_tra</strong> — inversion-translocation</li>
                    <li><span style={{ color: '#9030D0' }} aria-hidden="true">■</span> <strong>complex_inv</strong> — complex inversion</li>
                    <li><span style={{ color: '#2090C0' }} aria-hidden="true">■</span> <strong>foldback</strong> — foldback rearrangement</li>
                    <li><span style={{ color: '#888' }} aria-hidden="true">■</span> Other BND — grey</li>
                  </ul>
                </div>
              </div>
              <p style={{ ...s.para, marginTop: 12 }}>
                <strong>Tip:</strong> A Severus VCF can also be uploaded in the <strong>Sample File</strong> slot — its DEL and DUP records will be displayed as Loss and Gain bars, useful when you don't have a separate Wakhan CNA file.
              </p>
              <p style={s.para}>
                <strong>Chromosome prefix:</strong> VCF files use <code style={s.code}>chr1</code>, <code style={s.code}>chr2</code> … notation. Pre-loaded band files use <code style={s.code}>1</code>, <code style={s.code}>2</code> … The parser strips the prefix automatically so you don't need to pre-process your files.
              </p>
            </section>

            <hr style={s.rule} />

            {/* ── Downloading ─────────────────────────── */}
            <section id="help-download" aria-label="Downloading ideograms">
              <h2 style={s.h2}>↓ Downloading Your Ideogram</h2>
              <p style={s.para}>
                After generating, use the <strong>Download</strong> button in the top-right of the preview area. The image is rendered at full resolution (4200 × 8940 px for Linear; 3000 × 3000 px for Circular) regardless of how it appears in the browser preview.
              </p>
              <div style={s.colorGrid}>
                {[
                  { fmt: 'PNG',  desc: 'Lossless, transparent background — best for presentations and web' },
                  { fmt: 'PDF',  desc: 'Vector-backed — ideal for publications and print' },
                  { fmt: 'TIFF', desc: 'Uncompressed, high-quality — standard for clinical and journal submission' },
                  { fmt: 'JPG',  desc: 'Compressed — smaller file size for sharing' },
                  { fmt: 'BMP',  desc: 'Uncompressed bitmap' },
                  { fmt: 'GIF',  desc: 'Supports transparency, limited colour depth' },
                ].map(f => (
                  <div key={f.fmt} style={s.colorRow}>
                    <span style={{ ...s.swatch, background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--ink)', fontSize: '.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38 }}>
                      {f.fmt}
                    </span>
                    <span style={s.colorLabel}>{f.desc}</span>
                  </div>
                ))}
              </div>
            </section>

            <hr style={s.rule} />

            {/* ── FAQ ─────────────────────────────────── */}
            <section id="help-faq" aria-label="Frequently asked questions">
              <h2 style={s.h2}>? Frequently Asked Questions</h2>
              <div style={s.faqList} role="list">

                <Faq q="Why are my chromosomes blank / not showing any bands?" s={s}>
                  The cytoband file is required. If you selected a pre-loaded file, make sure the server is running and the Bands directory is accessible. If you uploaded a custom file, check that it is tab-delimited with the correct column names: <code style={s.code}>chromosome, start, end, bandname, stain</code>.
                </Faq>

                <Faq q="My sample events don't appear on the ideogram. What's wrong?" s={s}>
                  The most common cause is a chromosome name mismatch. If your band file uses <code style={s.code}>1, 2, 3…</code> then your sample file must use the same format — not <code style={s.code}>chr1, chr2…</code> (exception: VCF files strip the prefix automatically). Also check that the <code style={s.code}>type</code> column uses one of the recognised values: <strong>Gain, Loss, Deletion, Homo_Loss, CN_LOH</strong>.
                </Faq>

                <Faq q="What is the difference between Loss, Deletion, and Homo Loss?" s={s}>
                  <ul style={{ ...s.ul, marginTop: 6 }}>
                    <li><strong>Loss</strong> — one copy lost, one copy remains (CN = 1 in diploid). Shown in red.</li>
                    <li><strong>Deletion</strong> — treated visually the same as Loss by default, but kept as a separate type so you can colour or filter it independently.</li>
                    <li><strong>Homo Loss</strong> — <em>both</em> copies deleted, no functional copy remains (CN = 0). Shown in orange. This is the most severe state and is often associated with tumour suppressor gene inactivation.</li>
                  </ul>
                </Faq>

                <Faq q="What is CN LOH (copy-neutral loss of heterozygosity)?" s={s}>
                  CN LOH (also called uniparental disomy) is a region where the total copy number is normal (CN = 2) but both copies come from the same parent — one allele has been lost and the other has been duplicated. The region appears normal on coverage tracks but shows complete loss of heterozygosity on SNP arrays. Shown in green.
                </Faq>

                <Faq q="Why is the orange bar called Homo Loss and not Homozygous Deletion?" s={s}>
                  "Homo Loss" is shorthand for <em>homozygous loss</em> — loss of both alleles. "Homozygous deletion" and "biallelic deletion" are synonymous terms used in different communities. Ideochrom uses "Homo Loss" for brevity. You can restyle it independently via the Abnormality Colors panel.
                </Faq>

                <Faq q="Can I display multiple patients / samples on the same ideogram?" s={s}>
                  Yes. Each unique value in the <code style={s.code}>patientid</code> column of the sample file is rendered as a separate track (vertical stripe) alongside each chromosome. Tracks are stacked outward, so the first sample is closest to the chromosome body.
                </Faq>

                <Faq q="The translocation file slot doesn't appear. Where is it?" s={s}>
                  The Translocation File slot is only shown when <strong>Circular</strong> layout is selected. Switch to Circular in the Options section and the slot will appear in the Files section.
                </Faq>

                <Faq q="My Severus VCF has thousands of BND records — will the circular plot be readable?" s={s}>
                  With hundreds of arcs the plot can become dense. Most BND records in a typical somatic VCF have very low VAF (~5%) and may represent noise. You can pre-filter the VCF to keep only high-confidence, high-VAF events before uploading, or use the Abnormality Filter to suppress specific types.
                </Faq>

                <Faq q="How do I use a custom cytoband file (e.g. mouse, rat, or a non-standard assembly)?" s={s}>
                  Upload your own file in the <strong>Cytoband File</strong> slot using the "Custom" tab. The file must be tab-delimited with the five required columns. UCSC cytoBand.txt.gz files (decompressed) work directly after changing the extension to .txt. Mouse and rat are detected automatically from the chromosome names.
                </Faq>

                <Faq q="Can I change the colors of the band stains or abnormalities?" s={s}>
                  Yes — open the <strong>Abnormality Colors</strong> or <strong>Band Stain Colors</strong> sections in the sidebar. Click any color swatch to open a picker. Changes apply only to the current session; they are not saved between page reloads.
                </Faq>

                <Faq q="What resolution / DPI is the exported image?" s={s}>
                  The image canvas is rendered at 4200 × 8940 px (Linear) or 3000 × 3000 px (Circular). At 300 DPI that equals roughly A3 paper size for Linear and 25 × 25 cm for Circular — both suitable for journal submission. PDF export is resolution-independent.
                </Faq>

                <Faq q="My file upload shows an error about CSV headers. What does that mean?" s={s}>
                  The parser expects specific column names. Make sure headers are present on the first row, columns are separated by <strong>tabs</strong> (not commas or spaces), and the column names match exactly (case-insensitive). Download the example files (link in the Files section) for ready-to-use templates. If you are uploading a VCF, ensure it begins with <code style={s.code}>##fileformat=VCF</code> and the server has been restarted after any recent updates.
                </Faq>

                <Faq q="Is my data stored or sent to a third party?" s={s}>
                  No. All files are processed in memory on the server and discarded immediately after the image is generated. Patient identifiers and genomic data are never logged or stored.
                </Faq>

              </div>
            </section>

            <div style={{ height: 40 }} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ── Sub-components ────────────────────────────────────────── */

interface FileSectionProps {
  title: string;
  badge: string;
  badgeColor: string;
  desc: string;
  formats: string[];
  columns: { name: string; desc: string }[];
  note?: string;
  s: Styles;
}

const FileSection = ({ title, badge, badgeColor, desc, formats, columns, note, s }: FileSectionProps) => (
  <article style={s.fileSection} aria-label={`${title} file format`}>
    <div style={s.fileTitleRow}>
      <strong style={s.fileTitle}>{title}</strong>
      <span style={{ ...s.badge, background: badgeColor }}>{badge}</span>
    </div>
    <p style={s.fileDesc}>{desc}</p>
    <div style={s.formatRow}>
      {formats.map(f => <span key={f} style={s.formatTag}>{f}</span>)}
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={s.colTable}>
        <thead>
          <tr>
            <th style={s.colTh} scope="col">Column</th>
            <th style={s.colTh} scope="col">Description</th>
          </tr>
        </thead>
        <tbody>
          {columns.map(c => (
            <tr key={c.name}>
              <td style={s.colTdCode}><code style={s.code}>{c.name}</code></td>
              <td style={s.colTd}>{c.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {note && <p style={s.noteBox}>{note}</p>}
  </article>
);

interface FaqProps { q: string; children: React.ReactNode; s: Styles; }

const Faq = ({ q, children, s }: FaqProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={s.faqItem} role="listitem">
      <button
        style={s.faqQ}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span style={s.faqChevron} aria-hidden="true">{open ? '▾' : '▸'}</span>
        {q}
      </button>
      {open && <div style={s.faqA}>{children}</div>}
    </div>
  );
};

/* ── Styles (responsive) ───────────────────────────────────── */

type Styles = Record<string, React.CSSProperties>;

function makeStyles(isMobile: boolean): Styles {
  return {
    titleBar: {
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      padding: isMobile ? '12px 14px 10px' : '16px 20px 14px',
      borderBottom: '1.5px solid var(--border)',
      background: 'var(--paper)',
      flexShrink: 0,
    },
    titleMain: {
      fontFamily: 'var(--font-serif)', fontSize: isMobile ? '1.1rem' : '1.35rem',
      color: 'var(--ink)', display: 'block', lineHeight: 1.2, margin: 0,
    },
    titleSub: {
      fontFamily: 'var(--font-mono)', fontSize: '.65rem', color: 'var(--muted)',
      letterSpacing: '.06em', textTransform: 'uppercase', display: 'block', marginTop: 2,
    },

    // ── Nav ──────────────────────────────────────────────────
    nav: isMobile ? {
      display: 'flex', flexDirection: 'row', overflowX: 'auto', flexShrink: 0,
      borderBottom: '1.5px solid var(--border)',
      background: 'var(--panel)', padding: '6px 8px', gap: 4,
      scrollbarWidth: 'none',
    } : {
      width: 160, flexShrink: 0, borderRight: '1.5px solid var(--border)',
      background: 'var(--panel)', padding: '12px 0',
      display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto',
    },
    navBtn: isMobile ? {
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '6px 10px', border: '1px solid var(--border)',
      background: 'var(--paper)', borderRadius: 20,
      fontFamily: 'var(--font-sans)', fontSize: '.7rem', color: 'var(--ink)',
      cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all .12s',
    } : {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 14px', border: 'none', background: 'transparent',
      fontFamily: 'var(--font-sans)', fontSize: '.78rem', color: 'var(--ink)',
      cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all .12s',
      borderLeft: '3px solid transparent',
    },
    navBtnActive: isMobile ? {
      background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff', fontWeight: 600,
    } : {
      background: 'var(--paper)', borderLeftColor: 'var(--accent)',
      color: 'var(--accent)', fontWeight: 600,
    },
    navIcon: { fontSize: '.85rem', width: isMobile ? undefined : 16, textAlign: 'center', flexShrink: 0 },
    navLabelMobile: { display: 'none' },  // icon-only pill on very small screens — we keep label but trim with CSS

    // ── Body ─────────────────────────────────────────────────
    body: {
      flex: 1, overflowY: 'auto',
      padding: isMobile ? '18px 16px' : '28px 32px',
      background: 'var(--paper)',
    },
    h2: {
      fontFamily: 'var(--font-serif)', fontSize: isMobile ? '1.05rem' : '1.25rem',
      color: 'var(--ink)', marginBottom: 10, lineHeight: 1.3,
    },
    h3: {
      fontFamily: 'var(--font-mono)', fontSize: '.7rem', letterSpacing: '.1em',
      textTransform: 'uppercase', color: 'var(--muted)', margin: '18px 0 8px',
    },
    lead: { fontSize: '.88rem', color: 'var(--ink)', lineHeight: 1.65, marginBottom: 18 },
    para: { fontSize: '.83rem', color: 'var(--ink)', lineHeight: 1.7, marginBottom: 10 },
    rule: { border: 'none', borderTop: '1.5px solid var(--border)', margin: '28px 0' },

    stepsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',
      gap: 12, marginBottom: 4,
    },
    stepCard: {
      background: 'var(--panel)', borderRadius: 8, padding: '16px 14px',
      border: '1.5px solid var(--border)',
    },
    stepNum: {
      fontFamily: 'var(--font-serif)', fontSize: '1.6rem', color: 'var(--accent)',
      lineHeight: 1, marginBottom: 8, opacity: .6,
    },
    stepHead: { display: 'block', fontSize: '.82rem', color: 'var(--ink)', marginBottom: 6 },
    stepBody: { fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.55, margin: 0 },

    twoCol: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: 14,
    },
    card: {
      background: 'var(--panel)', border: '1.5px solid var(--border)',
      borderRadius: 8, padding: '16px 14px',
    },
    cardHead: { display: 'block', fontSize: '.84rem', color: 'var(--ink)', marginBottom: 8 },
    cardBody: { fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.6 },
    cardNote: { fontSize: '.72rem', color: 'var(--accent)', fontWeight: 600, marginTop: 10, lineHeight: 1.5 },

    dl: { display: 'flex', flexDirection: 'column', gap: 14 },
    dt: { fontWeight: 600, fontSize: '.83rem', color: 'var(--ink)', marginBottom: 3 },
    dd: { fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.6, marginLeft: 0 },

    colorGrid: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 },
    colorRow:  { display: 'flex', alignItems: 'center', gap: 12 },
    swatch: { width: 22, height: 22, borderRadius: 4, flexShrink: 0, display: 'inline-block' },
    colorLabel: { fontSize: '.8rem', color: 'var(--ink)', lineHeight: 1.4 },

    fileSection: { marginBottom: 28, paddingBottom: 22, borderBottom: '1px solid var(--border)' },
    fileTitleRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
    fileTitle: { fontSize: '.9rem', color: 'var(--ink)' },
    badge: {
      fontFamily: 'var(--font-mono)', fontSize: '.58rem', padding: '2px 7px',
      borderRadius: 3, color: '#fff', letterSpacing: '.05em',
    },
    fileDesc:  { fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 10 },
    formatRow: { display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' },
    formatTag: {
      fontFamily: 'var(--font-mono)', fontSize: '.66rem', padding: '2px 8px',
      background: 'var(--panel)', border: '1px solid var(--border)',
      borderRadius: 4, color: 'var(--ink)',
    },
    colTable:  { width: '100%', borderCollapse: 'collapse', marginBottom: 8, minWidth: isMobile ? 280 : undefined },
    colTh: {
      fontFamily: 'var(--font-mono)', fontSize: '.6rem', letterSpacing: '.1em',
      textTransform: 'uppercase', color: 'var(--muted)', textAlign: 'left',
      paddingBottom: 6, borderBottom: '1px solid var(--border)',
    },
    colTdCode: { padding: '6px 14px 6px 0', verticalAlign: 'top', whiteSpace: 'nowrap' },
    colTd: { padding: '6px 0', fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.5, verticalAlign: 'top' },
    noteBox: {
      fontSize: '.76rem', color: 'var(--blue)', lineHeight: 1.55,
      background: '#eff6ff', border: '1px solid #bfdbfe',
      borderRadius: 6, padding: '8px 12px', marginTop: 8,
    },

    code: {
      fontFamily: 'var(--font-mono)', fontSize: '.78em',
      background: 'var(--panel)', padding: '1px 5px',
      borderRadius: 3, border: '1px solid var(--border)',
    },
    ul: { paddingLeft: 18, fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.7 },

    faqList: { display: 'flex', flexDirection: 'column', gap: 0 },
    faqItem: { borderBottom: '1px solid var(--border)' },
    faqQ: {
      display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%',
      padding: '13px 0', border: 'none', background: 'transparent',
      fontFamily: 'var(--font-sans)', fontSize: '.84rem', fontWeight: 600,
      color: 'var(--ink)', cursor: 'pointer', textAlign: 'left', lineHeight: 1.4,
    },
    faqChevron: { color: 'var(--accent)', flexShrink: 0, marginTop: 2 },
    faqA: { fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.7, paddingBottom: 14, paddingLeft: 22 },
  };
}

export default HelpDialog;
